import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { welcomeWeekService } from '@/services/welcomeWeekService';
import { organizationService } from '@/services/organizationService';
import { DEFAULT_WW_SEASON_CODE, type WelcomeWeekPublicOverview } from '@/types/welcomeWeek';
import { formatEventTime, formatLocation, seasonQuery } from './wwUi';
import { ArrowLeft, CalendarDays, ExternalLink, GraduationCap, MapPin, Settings, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function WelcomeWeekPublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seasonCode = searchParams.get('season') || DEFAULT_WW_SEASON_CODE;
  const explicitSeason = searchParams.get('season');
  const [overview, setOverview] = useState<WelcomeWeekPublicOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  // ids of the groups the signed-in user belongs to (is a group admin of); null until resolved
  const [myGroupIds, setMyGroupIds] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const data = await welcomeWeekService.getPublicOverview(slug, seasonCode);
        if (!cancelled) setOverview(data);
      } catch (error) {
        console.error('[WelcomeWeekPublic] load failed', error);
        if (!cancelled) setOverview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, seasonCode]);

  useEffect(() => {
    let cancelled = false;
    const orgId = overview?.organization?.id;
    if (!orgId) {
      setCanManage(false);
      setMyGroupIds(null);
      return;
    }
    // Resolve the org role and the group-admin scoping independently: a failure
    // in the group-scoping lookup must NOT hide the owner/admin action buttons.
    (async () => {
      try {
        const memberships = await organizationService.myMemberships();
        if (cancelled) return;
        const m = memberships.find((item) => item.organization_id === orgId);
        setCanManage(m?.role === 'owner' || m?.role === 'admin');
      } catch {
        if (!cancelled) setCanManage(false);
      }
    })();
    (async () => {
      try {
        const myGroups = await welcomeWeekService.listMyAdminGroups(orgId);
        if (!cancelled) setMyGroupIds(myGroups.map((g) => g.id));
      } catch {
        if (!cancelled) setMyGroupIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [overview?.organization?.id]);

  // Owners/admins see every group; a user assigned to group(s) sees only those;
  // anyone with no group assignment (incl. anonymous visitors) sees all.
  const visibleGroups = useMemo(() => {
    const all = overview?.groups || [];
    if (canManage) return all;
    if (myGroupIds && myGroupIds.length > 0) {
      return all.filter((g) => myGroupIds.includes(g.id));
    }
    return all;
  }, [overview?.groups, canManage, myGroupIds]);
  const centralSource = useMemo(
    () => overview?.sources?.find((source) => source.source_key === 'central'),
    [overview?.sources],
  );

  // A member who belongs to exactly one group (and isn't an owner/admin) is
  // forwarded straight to that group's schedule -- they don't need the picker.
  // Owners/admins always see the full list.
  useEffect(() => {
    if (canManage || myGroupIds === null) return;
    const orgSlug = overview?.organization?.slug;
    const seasonForUrl = overview?.season?.code;
    if (!orgSlug || !seasonForUrl) return;
    const mine = (overview?.groups || []).filter((g) => myGroupIds.includes(g.id));
    if (mine.length === 1) {
      navigate(`/o/${orgSlug}/ww/${mine[0].slug}${seasonQuery(explicitSeason)}`, { replace: true });
    }
  }, [canManage, myGroupIds, overview?.organization?.slug, overview?.season?.code, overview?.groups, navigate, explicitSeason]);



  return (
    <PublicLayout>
      <div className="container py-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CalendarDays className="h-10 w-10 animate-pulse text-muted-foreground" />
          </div>
        ) : !overview?.organization || !overview.season ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarDays className="h-14 w-14 mx-auto text-muted-foreground" />
              <h1 className="mt-4 text-2xl font-semibold">Welcome week not found</h1>
            </CardContent>
          </Card>
        ) : (
          <>
            <div>
              <Button asChild variant="ghost" className="px-0">
                <Link to={`/o/${overview.organization.slug}`}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to {overview.organization.name}
                </Link>
              </Button>
            </div>

            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{overview.season.code}</Badge>
                  <Badge variant="outline">{visibleGroups.length} groups</Badge>
                </div>
                <h1 className="text-3xl font-bold tracking-tight">{overview.organization.name} Welcome Week</h1>
                <p className="text-muted-foreground">{overview.season.name}</p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {/* "Admin" used to sit here but linked to the same /ww/admin page
                    as the "Edit Master Schedule" card below, so it was redundant.
                    Only "Groups" (a distinct /ww/admin/groups destination) remains. */}
                {canManage && (
                  <Button asChild variant="outline">
                    <Link to={`/o/${overview.organization.slug}/ww/admin/groups${seasonQuery(explicitSeason)}`}>
                      <Users className="h-4 w-4 mr-2" />
                      Groups
                    </Link>
                  </Button>
                )}
                {canManage && centralSource && (
                  <Button asChild variant="outline">
                    <Link to={`/o/${overview.organization.slug}/ww/admin/central${seasonQuery(explicitSeason)}`}>
                      <GraduationCap className="h-4 w-4 mr-2" />
                      {centralSource.label}
                    </Link>
                  </Button>
                )}
                <Button asChild>
                  <a
                    href={`https://www.calone.com/${overview.organization.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CalendarDays className="h-4 w-4 mr-2" />
                    {overview.organization.name || 'Master'} Welcome Week Calendar
                  </a>
                </Button>
              </div>
            </div>

            {canManage && (
              <section className="space-y-3">
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold">{overview.organization.name || 'Master'} Welcome Week Schedule</h2>
                      <p className="text-sm text-muted-foreground">Manage and edit the central welcome week programme events shared across all groups.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto">
                      <Button asChild className="w-full sm:w-auto">
                        <Link to={`/o/${overview.organization.slug}/ww/admin${seasonQuery(explicitSeason)}`}>
                          <Settings className="h-4 w-4 mr-2" />
                          Edit {overview.organization.name || 'Master'} Schedule
                        </Link>
                      </Button>
                      {overview.organization.slug === 'omega' && (
                        <Button asChild variant="outline" className="border-primary/30 w-full sm:w-auto">
                          <Link to={`/o/omega/ww/admin2${seasonQuery(explicitSeason)}`}>
                            <Settings className="h-4 w-4 mr-2" />
                            Edit Omega2 Schedule
                          </Link>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            <section className="space-y-3">
              <h2 className="text-xl font-semibold tracking-tight">Groups</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {visibleGroups.map((group) => (
                  <Card key={group.id}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between gap-3 text-lg">
                        <span>{group.name}</span>
                        <Badge>{group.code}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                        <Users className="h-4 w-4" />
                        <span className="truncate">{group.slug}</span>
                      </div>
                      <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                        <Link to={`/o/${overview.organization?.slug}/ww/${group.slug}${seasonQuery(explicitSeason)}`}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
